import { useEffect, useState } from "react";
import { Link, Outlet, useNavigate } from "react-router-dom";
import { MessageCircle, X, Minus, Plus, Trash2 } from "lucide-react";
import Header from "./Header";
import Footer from "./Footer";
import { useStore } from "@/context/StoreContext";
import { brl, img } from "@/lib/api";

export const waLink = (settings, text) => {
  const n = settings?.whatsapp?.number;
  return n ? `https://wa.me/${n}?text=${encodeURIComponent(text || settings?.whatsapp?.message || "")}` : null;
};

function Marketing({ m, consent }) {
  useEffect(() => {
    if (!m || !consent) return;
    const add = (src, inline) => {
      const s = document.createElement("script");
      if (src) { s.src = src; s.async = true; } else s.text = inline;
      document.head.appendChild(s);
    };
    if (m.gtm_id) add(null, `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${m.gtm_id}');`);
    const gid = m.ga4_id || m.google_ads_id;
    if (gid) {
      add(`https://www.googletagmanager.com/gtag/js?id=${gid}`);
      add(null, `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}window.gtag=gtag;gtag('js',new Date());${m.ga4_id ? `gtag('config','${m.ga4_id}');` : ""}${m.google_ads_id ? `gtag('config','${m.google_ads_id}');` : ""}`);
    }
    if (m.meta_pixel_id) add(null, `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${m.meta_pixel_id}');fbq('track','PageView');`);
  }, [m, consent]);
  return null;
}

function CookieBanner({ onChange }) {
  const [v, setV] = useState(localStorage.getItem("e3_cookies"));
  if (v) return null;
  const pick = (x) => { localStorage.setItem("e3_cookies", x); setV(x); onChange(x); };
  return (
    <div className="fixed bottom-3 left-3 right-3 md:right-auto md:max-w-md z-50 card p-4 md:p-5 shadow-2xl no-print" role="dialog" aria-label="Cookies" data-testid="cookie-banner">
      <p className="text-xs md:text-sm text-[#E6E6E6]">Usamos cookies para melhorar sua experiência, conforme a LGPD. Veja a <Link to="/politicas/cookies" className="text-[#00C8FF] underline">Política de Cookies</Link>.</p>
      <div className="flex gap-2 mt-3">
        <button className="btn-primary btn-sm flex-1" onClick={() => pick("all")} data-testid="cookie-accept">Aceitar todos</button>
        <button className="btn-ghost btn-sm flex-1" onClick={() => pick("essential")} data-testid="cookie-essential">Somente essenciais</button>
      </div>
    </div>
  );
}

function CartDrawer() {
  const { cart, cartOpen, setCartOpen, updateQty, removeItem } = useStore();
  const nav = useNavigate();
  if (!cartOpen) return null;
  const subtotal = cart.reduce((s, i) => s + i.unit_price * i.qty, 0);
  return (
    <div className="fixed inset-0 z-50" data-testid="cart-drawer">
      <div className="absolute inset-0 bg-black/70" onClick={() => setCartOpen(false)} />
      <aside className="absolute right-0 top-0 h-full w-full max-w-md bg-[#0B0D11] border-l border-[#282D37] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-[#282D37]">
          <h2 className="text-lg font-semibold">Seu carrinho</h2>
          <button onClick={() => setCartOpen(false)} aria-label="Fechar" className="h-10 w-10 grid place-items-center rounded-xl hover:bg-[#171A1F]" data-testid="cart-drawer-close"><X className="h-5 w-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {cart.length === 0 && <p className="text-[#A0A0A0] text-sm py-10 text-center">Seu carrinho está vazio.</p>}
          {cart.map((i) => (
            <div key={i.key} className="flex gap-3" data-testid="drawer-item">
              <img src={img(i.image)} alt={i.name} className="h-20 w-20 rounded-xl object-cover" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm truncate">{i.name}</div>
                <div className="text-xs text-[#A0A0A0] truncate">{[...Object.values(i.colors || {}), ...Object.values(i.options || {})].filter(Boolean).join(" · ")}</div>
                <div className="flex items-center justify-between mt-2">
                  <div className="flex items-center border border-[#282D37] rounded-lg">
                    <button className="p-2" aria-label="Diminuir" onClick={() => updateQty(i.key, i.qty - 1)}><Minus className="h-3 w-3" /></button>
                    <span className="w-6 text-center text-sm">{i.qty}</span>
                    <button className="p-2" aria-label="Aumentar" onClick={() => updateQty(i.key, i.qty + 1)}><Plus className="h-3 w-3" /></button>
                  </div>
                  <span className="font-semibold text-sm">{brl(i.unit_price * i.qty)}</span>
                  <button onClick={() => removeItem(i.key)} aria-label="Remover" className="text-[#A0A0A0] hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
        {cart.length > 0 && (
          <div className="p-5 border-t border-[#282D37] space-y-3">
            <div className="flex justify-between"><span className="text-[#A0A0A0]">Subtotal</span><span className="font-bold">{brl(subtotal)}</span></div>
            <button className="btn-primary w-full py-4" onClick={() => { setCartOpen(false); nav("/carrinho"); }} data-testid="drawer-go-cart">Ver carrinho e finalizar</button>
          </div>
        )}
      </aside>
    </div>
  );
}

export default function StoreLayout() {
  const { settings } = useStore();
  const [consent, setConsent] = useState(localStorage.getItem("e3_cookies") === "all");
  const wa = waLink(settings);
  return (
    <div className="min-h-screen flex flex-col">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:p-3 focus:bg-[#008CFF]">Pular para o conteúdo</a>
      <Header />
      <main id="main" className="flex-1"><Outlet /></main>
      <Footer />
      <CartDrawer />
      <CookieBanner onChange={(x) => setConsent(x === "all")} />
      <Marketing m={settings?.marketing} consent={consent} />
      {wa && (
        <a href={wa} target="_blank" rel="noreferrer" aria-label="Fale conosco no WhatsApp" className="fixed bottom-5 right-5 z-40 h-14 w-14 rounded-full bg-[#25D366] grid place-items-center shadow-xl hover:scale-105 transition-transform no-print" data-testid="whatsapp-float">
          <MessageCircle className="h-7 w-7 text-white" />
        </a>
      )}
    </div>
  );
}
